import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, vModelText as _vModelText, createElementVNode as _createElementVNode, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withModifiers as _withModifiers, vModelSelect as _vModelSelect, normalizeStyle as _normalizeStyle, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "relative group border-2 border-primary rounded-md bg-card shadow-sm min-w-[200px] flex flex-col font-mono text-sm overflow-hidden" }
const _hoisted_2 = { class: "bg-primary/10 p-2 text-center border-b border-primary flex flex-col gap-1" }
const _hoisted_3 = ["onUpdate:modelValue"]
const _hoisted_4 = ["onUpdate:modelValue"]
const _hoisted_5 = { class: "p-1 border-b border-primary bg-yellow-100/50 dark:bg-yellow-900/20" }
const _hoisted_6 = ["onUpdate:modelValue"]
const _hoisted_7 = { class: "p-2 min-h-[40px] border-b border-primary flex flex-col gap-1 bg-card" }
const _hoisted_8 = ["onUpdate:modelValue"]
const _hoisted_9 = ["onClick"]
const _hoisted_10 = ["onClick"]
const _hoisted_11 = { class: "p-2 min-h-[40px] flex flex-col gap-1 bg-card" }
const _hoisted_12 = ["onUpdate:modelValue"]
const _hoisted_13 = ["onClick"]
const _hoisted_14 = ["onClick"]
const _hoisted_15 = { class: "absolute -top-3 -right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20" }
const _hoisted_16 = ["onClick"]
const _hoisted_17 = { class: "flex items-center gap-1" }
const _hoisted_18 = ["onUpdate:modelValue"]
const _hoisted_19 = ["onUpdate:modelValue"]
const _hoisted_20 = ["onUpdate:modelValue"]
const _hoisted_21 = ["onUpdate:modelValue"]
const _hoisted_22 = ["onClick"]
const _hoisted_23 = { class: "text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0" }

import { ref, watch } from 'vue'
import { 
  VueFlow, 
  useVueFlow, 
  Handle, 
  Position, 
  BaseEdge, 
  EdgeLabelRenderer, 
  getBezierPath
} from '@vue-flow/core'
import { Background } from '@vue-flow/background'
import { Controls } from '@vue-flow/controls'
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'
import '@vue-flow/controls/dist/style.css'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Square, Trash2, Copy, Check, RefreshCw, Code2, PlusSquare } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'DiagramEditorDialog',
  props: {
    open: { type: Boolean }
  },
  emits: ["update:open", "save"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const nodes = ref<any[]>([])
const edges = ref<any[]>([])
const showCode = ref(false)
const copied = ref(false)
const editableCode = ref('')
const isParsing = ref(false)

// Inicializar lienzo
const initNodes = () => {
  nodes.value = [{ 
    id: 'n-Animal', type: 'classNode', position: { x: 200, y: 50 }, 
    data: { name: 'Animal', annotation: 'interface', note: 'Esta es la clase base', properties: ['+int age', '+String gender'], methods: ['+isMammal() bool', '+mate()'] } 
  }]
  edges.value = []
}

// Inicializar al abrir
watch(() => props.open, (val) => {
  if (val) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (val && nodes.value.length === 0) initNodes()
}, { immediate: true })

const { onConnect, removeNodes } = useVueFlow()

const handleConnect = (params: any) => {
  edges.value.push({
    id: `e-${params.source}-${params.target}-${Date.now()}`,
    source: params.source,
    target: params.target,
    type: 'custom',
    data: { 
      relation: '<|--', // Herencia por defecto
      label: '',
      cardinalitySource: '',
      cardinalityTarget: ''
    }
  })
}

const addClassNode = () => {
  const id = `n-Clase_${Date.now()}`
  nodes.value.push({
    id,
    type: 'classNode',
    position: { x: Math.random() * 200 + 100, y: Math.random() * 200 + 100 },
    data: { name: `Clase_${Math.floor(Math.random()*1000)}`, annotation: '', note: '', properties: [], methods: [] }
  })
}

const deleteNode = (id: string) => {
  nodes.value = nodes.value.filter(n => n.id !== id)
  edges.value = edges.value.filter(e => e.source !== id && e.target !== id)
}

const deleteEdge = (id: string) => {
  edges.value = edges.value.filter(e => e.id !== id)
}

const generateCodeString = () => {
  let mermaid = '\n```mermaid\n'
  
  // Guardar metadata de posiciones (oculto en comentario Mermaid)
  const positions: Record<string, any> = {}
  nodes.value.forEach(node => {
    // node.id is something like n-Animal, but wait! The ID in DiagramEditorDialog is `n-${name}`.
    positions[node.id] = { x: Math.round(node.position.x), y: Math.round(node.position.y) }
  })
  mermaid += `%% {"positions": ${JSON.stringify(positions)}}\n`
  
  mermaid += 'classDiagram\n'
  
  // Generar relaciones (Edges)
  edges.value.forEach(edge => {
    const rel = edge.data?.relation || '<|--'
    const lbl = edge.data?.label?.trim()
    const cardS = edge.data?.cardinalitySource?.trim()
    const cardT = edge.data?.cardinalityTarget?.trim()
    
    const sourceNode = nodes.value.find(n => n.id === edge.source)
    const targetNode = nodes.value.find(n => n.id === edge.target)
    
    if (sourceNode && targetNode) {
      let line = `  ${sourceNode.data.name}`
      if (cardS) line += ` "${cardS}"`
      line += ` ${rel}`
      if (cardT) line += ` "${cardT}"`
      line += ` ${targetNode.data.name}`
      if (lbl) line += ` : ${lbl}`
      
      mermaid += line + '\n'
    }
  })

  // Generar las clases y sus miembros (Nodos)
  nodes.value.forEach(node => {
    mermaid += `  class ${node.data.name}{\n`
    if (node.data.annotation && node.data.annotation.trim()) {
      mermaid += `    <<${node.data.annotation.trim()}>>\n`
    }
    node.data.properties.forEach((prop: string) => {
      if (prop.trim()) mermaid += `    ${prop.trim()}\n`
    })
    node.data.methods.forEach((met: string) => {
      if (met.trim()) mermaid += `    ${met.trim()}\n`
    })
    mermaid += `  }\n`
    if (node.data.note && node.data.note.trim()) {
      mermaid += `  note for ${node.data.name} "${node.data.note.trim()}"\n`
    }
  })

  mermaid += '```\n'
  return mermaid
}

// Escuchar cambios en el UI visual para actualizar el código texto
watch([nodes, edges], () => {
  if (isParsing.value) return
  editableCode.value = generateCodeString()
}, { deep: true, immediate: true })

// Parsear Mermaid a VueFlow
const syncToVisual = () => {
  isParsing.value = true
  try {
    const lines = editableCode.value.split('\n')
    const parsedNodes = new Map() // name -> { properties, methods }
    const parsedEdges: any[] = []
    let savedPositions: Record<string, any> = {}

    const getNodeData = (name: string) => {
      if (!parsedNodes.has(name)) parsedNodes.set(name, { properties: [], methods: [], annotation: '', note: '' })
      return parsedNodes.get(name)
    }

    let inClassBlock: string | null = null

    lines.forEach(line => {
      line = line.trim()
      
      // Parsear JSON oculto de posiciones
      if (line.startsWith('%% {"positions":')) {
        try {
          savedPositions = JSON.parse(line.replace(/^%% /, '')).positions
        } catch (e) {
          console.error('Error parseando metadata de posiciones', e)
        }
        return
      }
      
      if (!line || line.startsWith('```') || line.startsWith('classDiagram') || line.startsWith('%%')) return

      // Relaciones
      const relations = ['<|--', '*--', 'o--', '-->', '..>', '..|>', '--']
      let foundRel = relations.find(r => line.includes(r))
      
      if (foundRel && !inClassBlock) {
        const parts = line.split(foundRel)
        let left = parts[0].trim()
        let right = parts[1].trim()
        
        let cardS = '', cardT = '', label = ''
        
        if (right.includes(':')) {
          const rParts = right.split(':')
          label = rParts[1].trim()
          right = rParts[0].trim()
        }
        
        let rightMatch = right.match(/^"([^"]+)"\s+(.+)$/)
        if (rightMatch) {
          cardT = rightMatch[1]
          right = rightMatch[2].trim()
        }
        
        let leftMatch = left.match(/^(.+)\s+"([^"]+)"$/)
        if (leftMatch) {
          left = leftMatch[1].trim()
          cardS = leftMatch[2]
        }

        getNodeData(left)
        getNodeData(right)
        
        parsedEdges.push({
          sourceName: left, targetName: right, relation: foundRel, cardinalitySource: cardS, cardinalityTarget: cardT, label
        })
        return
      }

      // Bloques de clase: class Nombre {
      if (line.startsWith('class ') && line.endsWith('{')) {
        inClassBlock = line.substring(6, line.length - 1).trim()
        getNodeData(inClassBlock)
        return
      }

      if (inClassBlock && line === '}') {
        inClassBlock = null
        return
      }

      if (inClassBlock) {
        const node = getNodeData(inClassBlock)
        if (line.startsWith('<<') && line.endsWith('>>')) {
          node.annotation = line.substring(2, line.length - 2).trim()
        } else if (line.includes('(') && line.includes(')')) {
          node.methods.push(line)
        } else {
          node.properties.push(line)
        }
        return
      }
      
      const noteMatch = line.match(/^note\s+for\s+([^\s]+)\s+"([^"]+)"$/)
      if (noteMatch) {
         const node = getNodeData(noteMatch[1])
         node.note = noteMatch[2]
         return
      }

      // Linea suelta: ClassName : +prop
      if (line.includes(':') && !foundRel) {
        const parts = line.split(':')
        const name = parts[0].trim()
        if (name !== 'class') {
          const val = parts[1].trim()
          const node = getNodeData(name)
          if (val.includes('(') && val.includes(')')) node.methods.push(val)
          else node.properties.push(val)
        }
      }
    })

    // Reconstruir visuales
    const newNodes: any[] = []
    let currentX = 100, currentY = 100
    
    parsedNodes.forEach((data, name) => {
      const nodeId = `n-${name}`
      // Intentar mantener posición si existía en visual o usar la guardada en el comment
      const existing = nodes.value.find(n => n.id === nodeId)
      let pos = existing ? existing.position : { x: currentX, y: currentY }
      
      if (!existing && savedPositions[nodeId]) {
        pos = { x: savedPositions[nodeId].x, y: savedPositions[nodeId].y }
      } else if (!existing) { 
        currentX += 30; currentY += 30 
      }
      
      newNodes.push({
        id: `n-${name}`, type: 'classNode', position: pos,
        data: { name, properties: data.properties, methods: data.methods, annotation: data.annotation, note: data.note }
      })
    })

    const newEdges = parsedEdges.map((e, i) => ({
      id: `e-${e.sourceName}-${e.targetName}-${i}`,
      source: `n-${e.sourceName}`,
      target: `n-${e.targetName}`,
      type: 'custom',
      data: { relation: e.relation, cardinalitySource: e.cardinalitySource, cardinalityTarget: e.cardinalityTarget, label: e.label }
    }))

    nodes.value = newNodes
    edges.value = newEdges
  } catch (e) {
    console.error('Error parseando Mermaid:', e)
  }
  
  // Pequeño delay para reactivar el watcher sin loop
  setTimeout(() => isParsing.value = false, 100)
}

let debounceTimer: ReturnType<typeof setTimeout>
const onCodeChange = () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    syncToVisual()
  }, 400) // 400ms delay para auto-sincronizar mientras escribe
}

const generateMermaid = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
  initNodes()
}

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch (err) {
    console.error('Failed to copy text', err)
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseVisualEditor, {
    open: __props.open,
    "onUpdate:open": _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('update:open', $event))),
    title: "Diagrama de Clases (UML)",
    description: "Diseña la estructura orientada a objetos visualmente. Añade clases, propiedades, métodos y relaciones (herencia, composición, etc.).",
    showCode: showCode.value,
    "onUpdate:showCode": _cache[4] || (_cache[4] = ($event: any) => (showCode.value = $event)),
    copied: copied.value,
    onCopy: copyToClipboard,
    onSave: generateMermaid
  }, {
    icon: _withCtx(() => [
      _createVNode(_unref(Square), { class: "w-5 h-5 text-primary" })
    ]),
    toolbar: _withCtx(() => [
      _createVNode(_unref(Button), {
        size: "sm",
        onClick: addClassNode
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(PlusSquare), { class: "w-4 h-4 mr-2" }),
          _cache[5] || (_cache[5] = _createTextVNode(" Añadir Clase", -1))
        ]),
        _: 1
      })
    ]),
    canvas: _withCtx(() => [
      _createVNode(_unref(VueFlow), {
        nodes: nodes.value,
        "onUpdate:nodes": _cache[0] || (_cache[0] = ($event: any) => ((nodes).value = $event)),
        edges: edges.value,
        "onUpdate:edges": _cache[1] || (_cache[1] = ($event: any) => ((edges).value = $event)),
        onConnect: handleConnect,
        "default-zoom": 1
      }, {
        "node-classNode": _withCtx((nodeProps) => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": ($event: any) => ((nodeProps.data.annotation) = $event),
                class: "bg-transparent text-center w-full focus:outline-none text-xs text-primary italic",
                placeholder: "<<annotation>>"
              }, null, 8, _hoisted_3), [
                [_vModelText, nodeProps.data.annotation]
              ]),
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": ($event: any) => ((nodeProps.data.name) = $event),
                class: "bg-transparent text-center w-full focus:outline-none font-bold",
                placeholder: "ClassName"
              }, null, 8, _hoisted_4), [
                [_vModelText, nodeProps.data.name]
              ])
            ]),
            _createElementVNode("div", _hoisted_5, [
              _withDirectives(_createElementVNode("textarea", {
                "onUpdate:modelValue": ($event: any) => ((nodeProps.data.note) = $event),
                class: "bg-transparent text-[10px] w-full focus:outline-none resize-none text-center",
                placeholder: "Añadir nota de clase...",
                rows: "1"
              }, null, 8, _hoisted_6), [
                [_vModelText, nodeProps.data.note]
              ])
            ]),
            _createElementVNode("div", _hoisted_7, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(nodeProps.data.properties, (prop, i) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: i,
                  class: "flex items-center group/item"
                }, [
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": ($event: any) => ((nodeProps.data.properties[i]) = $event),
                    class: "bg-transparent text-xs w-full focus:outline-none focus:bg-muted/50 rounded px-1",
                    placeholder: "+type name"
                  }, null, 8, _hoisted_8), [
                    [_vModelText, nodeProps.data.properties[i]]
                  ]),
                  _createElementVNode("button", {
                    onClick: ($event: any) => (nodeProps.data.properties.splice(i, 1)),
                    class: "text-destructive opacity-0 group-hover/item:opacity-100"
                  }, [
                    _createVNode(_unref(Trash2), { class: "w-3 h-3" })
                  ], 8, _hoisted_9)
                ]))
              }), 128)),
              _createElementVNode("button", {
                onClick: ($event: any) => (nodeProps.data.properties.push('+newProp')),
                class: "text-[10px] text-muted-foreground hover:text-primary text-left mt-1"
              }, "+ Añadir propiedad", 8, _hoisted_10)
            ]),
            _createElementVNode("div", _hoisted_11, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(nodeProps.data.methods, (method, i) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: i,
                  class: "flex items-center group/item"
                }, [
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": ($event: any) => ((nodeProps.data.methods[i]) = $event),
                    class: "bg-transparent text-xs w-full focus:outline-none focus:bg-muted/50 rounded px-1",
                    placeholder: "+method()"
                  }, null, 8, _hoisted_12), [
                    [_vModelText, nodeProps.data.methods[i]]
                  ]),
                  _createElementVNode("button", {
                    onClick: ($event: any) => (nodeProps.data.methods.splice(i, 1)),
                    class: "text-destructive opacity-0 group-hover/item:opacity-100"
                  }, [
                    _createVNode(_unref(Trash2), { class: "w-3 h-3" })
                  ], 8, _hoisted_13)
                ]))
              }), 128)),
              _createElementVNode("button", {
                onClick: ($event: any) => (nodeProps.data.methods.push('+newMethod()')),
                class: "text-[10px] text-muted-foreground hover:text-primary text-left mt-1"
              }, "+ Añadir método", 8, _hoisted_14)
            ]),
            _createVNode(_unref(Handle), {
              type: "target",
              position: _unref(Position).Top,
              id: "top"
            }, null, 8, ["position"]),
            _createVNode(_unref(Handle), {
              type: "source",
              position: _unref(Position).Bottom,
              id: "bottom"
            }, null, 8, ["position"]),
            _createVNode(_unref(Handle), {
              type: "target",
              position: _unref(Position).Left,
              id: "left"
            }, null, 8, ["position"]),
            _createVNode(_unref(Handle), {
              type: "source",
              position: _unref(Position).Right,
              id: "right"
            }, null, 8, ["position"]),
            _createElementVNode("div", _hoisted_15, [
              _createElementVNode("button", {
                onClick: _withModifiers(($event: any) => (deleteNode(nodeProps.id)), ["stop"]),
                class: "bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center hover:scale-110 shadow-sm",
                title: "Eliminar Clase"
              }, "×", 8, _hoisted_16)
            ])
          ])
        ]),
        "edge-custom": _withCtx((edgeProps) => [
          _createVNode(_unref(BaseEdge), {
            id: edgeProps.id,
            path: _unref(getBezierPath)(edgeProps)[0],
            class: "vue-flow__edge-path",
            style: { strokeWidth: 2, stroke: 'hsl(var(--primary))' }
          }, null, 8, ["id", "path"]),
          _createVNode(_unref(EdgeLabelRenderer), null, {
            default: _withCtx(() => [
              _createElementVNode("div", {
                style: _normalizeStyle({
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${_unref(getBezierPath)(edgeProps)[1]}px,${_unref(getBezierPath)(edgeProps)[2]}px)`,
                pointerEvents: 'all',
              }),
                class: "nodrag nopan bg-card border shadow-md rounded p-2 group z-10 flex flex-col gap-2 w-[180px]"
              }, [
                _createElementVNode("div", _hoisted_17, [
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": ($event: any) => ((edgeProps.data.cardinalitySource) = $event),
                    class: "w-8 text-center text-xs border rounded bg-background p-1 focus:ring-1 focus:ring-primary outline-none",
                    title: "Cardinalidad Origen",
                    placeholder: "1"
                  }, null, 8, _hoisted_18), [
                    [_vModelText, edgeProps.data.cardinalitySource]
                  ]),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": ($event: any) => ((edgeProps.data.relation) = $event),
                    class: "flex-1 text-xs border rounded p-1 bg-background focus:ring-1 focus:ring-primary outline-none",
                    title: "Tipo de Relación"
                  }, [...(_cache[6] || (_cache[6] = [
                    _createElementVNode("option", { value: "<|--" }, "Herencia <|--", -1),
                    _createElementVNode("option", { value: "*--" }, "Composición *--", -1),
                    _createElementVNode("option", { value: "o--" }, "Agregación o--", -1),
                    _createElementVNode("option", { value: "-->" }, "Asociación -->", -1),
                    _createElementVNode("option", { value: "..>" }, "Dependencia ..>", -1),
                    _createElementVNode("option", { value: "..|>" }, "Realización ..|>", -1),
                    _createElementVNode("option", { value: "--" }, "Enlace --", -1)
                  ]))], 8, _hoisted_19), [
                    [_vModelSelect, edgeProps.data.relation]
                  ]),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": ($event: any) => ((edgeProps.data.cardinalityTarget) = $event),
                    class: "w-8 text-center text-xs border rounded bg-background p-1 focus:ring-1 focus:ring-primary outline-none",
                    title: "Cardinalidad Destino",
                    placeholder: "*"
                  }, null, 8, _hoisted_20), [
                    [_vModelText, edgeProps.data.cardinalityTarget]
                  ])
                ]),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": ($event: any) => ((edgeProps.data.label) = $event),
                  class: "w-full bg-background text-center border rounded p-1 text-xs text-foreground focus:ring-1 focus:ring-primary outline-none",
                  placeholder: "Etiqueta (opcional)..."
                }, null, 8, _hoisted_21), [
                  [_vModelText, edgeProps.data.label]
                ]),
                _createElementVNode("button", {
                  onClick: ($event: any) => (deleteEdge(edgeProps.id)),
                  class: "absolute -top-2 -right-2 bg-destructive text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity shadow-sm",
                  title: "Eliminar Conexión"
                }, "×", 8, _hoisted_22)
              ], 4)
            ]),
            _: 2
          }, 1024)
        ]),
        default: _withCtx(() => [
          _createVNode(_unref(Background)),
          _createVNode(_unref(Controls))
        ]),
        _: 1
      }, 8, ["nodes", "edges"])
    ]),
    "code-panel": _withCtx(() => [
      _createElementVNode("div", _hoisted_23, [
        _cache[8] || (_cache[8] = _createTextVNode(" Mermaid (Sincronizado) ", -1)),
        _createVNode(_unref(Button), {
          size: "sm",
          variant: "default",
          class: "h-7 text-xs",
          onClick: syncToVisual
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(RefreshCw), { class: "w-3 h-3 mr-2" }),
            _cache[7] || (_cache[7] = _createTextVNode(" Sincronizar a Visual ", -1))
          ]),
          _: 1
        })
      ]),
      _createVNode(MermaidCodeEditor, {
        modelValue: editableCode.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((editableCode).value = $event)),
        class: "flex-1 min-h-0"
      }, null, 8, ["modelValue"])
    ]),
    "live-preview": _withCtx(() => [
      _createVNode(MermaidLivePreview, { code: editableCode.value }, null, 8, ["code"])
    ]),
    _: 1
  }, 8, ["open", "showCode", "copied"]))
}
}

})